import { IkeFieldType } from '@ikegps/core-api';
import {
	type FormModel,
	type FormModelField,
	FormModelFieldLine,
	FormModelFieldNestedList,
	FormModelFieldSelectList,
	FormModelFieldSubForm,
	IkeId,
} from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { IkePageName } from '../../page/page';
import { Services } from '../../services';
import type { FormEditState } from '../../state/form.edit.state';
import { AppStore } from '../../store';
import { FormFieldEditor } from './editor/form.field.editor';
import { FormFieldEditorForm } from './editor/form.field.editor.form';
import { FormFieldEditorHeight } from './editor/form.field.editor.height';
import { FormFieldEditorLine } from './editor/form.field.editor.line';
import { FormFieldEditorNestedList } from './editor/form.field.editor.nested.list';
import { FormFieldEditorSelectList } from './editor/form.field.editor.select.list';
import { FormFieldEditorSubForm } from './editor/subform/form.field.editor.subform';
import { FormFieldEditorVerticalSeparation } from './editor/form.field.editor.vertical.separation';
import { CSS, Style } from './form.designer.style';

addStyles(Style);

export class FormDesigner extends IMithrilStateComponent {
	@observable isEditing: boolean = false;
	@observable isSaving: boolean = false;
	@observable isNewForm: boolean = false;

	@observable.ref form?: FormModel = undefined;

	@observable selectedFieldId: string | null = null;
	@observable scrollToFieldId: string | null = null;

	private fields: { [key: string]: FormFieldEditor } = {};

	constructor() {
		super();

		makeObservable(this);

		Services.Window.bindNavigateAway(T.DISCARD_CHANGES_CONFIRM, this.checkExit);
	}

	get nodes(): MithrilVNode {
		return m('div', { className: `${CSS.container} ${this.isEditing ? CSS.isEditing : CSS.isReadOnly}` }, [
			this.viewHeader(),
			m('div', { className: CSS.content }, [this.viewFields(), this.viewInfo()]),
		]);
	}

	override onRemove() {
		super.onRemove();

		Services.Window.unbindNavigateAway(this.checkExit);

		// Detach cached editors from the (potentially shared) editState.inputStates so their
		// reactions and stale validation state can't leak into a subsequent FormDesigner that
		// reads from the same editState (e.g. /form/new → /form/edit/:id after save).
		const editState = this.editState;
		if (editState) {
			for (const fieldId of Object.keys(this.fields)) {
				const editor = this.fields[fieldId];
				if (editor) {
					editState.inputStates.remove(editor.state);
					editor.destroy();
				}
			}
		}
		this.fields = {};

		this.editState?.destroy();
	}

	@action setForm(form: FormModel) {
		this.form = AppStore.Form.edit(form)?.form;

		// After saving a new form, clear the edit state.
		// Use the new form edit state despite the versions being the same.
		if (this.form?.id == null && form.id != null) {
			AppStore.Form.clearEdit(form);
			this.form = AppStore.Form.edit(form)?.form;
		}

		this.reset();
	}

	@action setEditing(isEditing: boolean) {
		this.isEditing = isEditing;
	}

	get editState(): FormEditState | null {
		if (this.form && this.isEditing) {
			return AppStore.Form.edit(this.form);
		}
		return null;
	}

	private viewHeader() {
		const headerButtons: MithrilVNode[] = [];

		if (this.isNewForm) {
			// If the user is internal then they are allowed to clear all fields.
			if (Services.User.isIkeAnalyst(Services.User.current)) {
				headerButtons.push(
					m(
						'button',
						{
							className: 'mdl-button',
							onclick: () => this.clearAllFields(),
						},
						Translate(T.CLEAR_ALL_FIELDS)
					)
				);
			}
			// Otherwise the will need to contact support
			else {
				headerButtons.push(
					m('div', { className: CSS.headerMsg }, [
						m('span', [
							`${Translate(T.CLEAR_FIELDS_HELP)} `,
							m(
								'a',
								{
									target: '_blank',
									href: 'https://support.ikegps.com/hc/en-us/requests/new?ticket_form_id=5786678993677',
								},
								Translate(T.HERE)
							),
						]),
					])
				);
			}

			headerButtons.push(
				m(
					'button',
					{
						className: 'mdl-button',
						onclick: () => this.clearUnlockedFields(),
					},
					Translate(T.CLEAR_UNLOCKED_FIELDS)
				)
			);
		}

		headerButtons.push(
			m(
				'button',
				{
					className: 'mdl-button mdl-button--raised mdl-button--accent',
					disabled: !this.canSave,
					onclick: () => this.save(),
				},
				this.isSaving ? Spinner.view() : Translate(T.SAVE)
			)
		);

		return m('div', { className: CSS.header }, [
			m('h6', Translate(T.FORM_EDIT_TITLE)),
			m('div', { style: 'display: flex; gap: 1.5em' }, headerButtons),
		]);
	}

	@action public clearUnlockedFields() {
		for (const field of this.form?.getFieldsRecursive ?? []) {
			if (!field.isLocked) {
				// Clean up the editor state before deleting the field
				const editor = this.fields[field.fieldId];
				if (editor) {
					editor.destroy();
					delete this.fields[field.fieldId];
				}
				field.Mutator.delete();
			}
		}
		// Clear selection if the selected field was deleted
		if (this.form) {
			this.selectedFieldId = this.form.fieldId;
		}
	}

	@action public clearAllFields() {
		if (this.form) {
			for (const field of this.form.getFieldsRecursive) {
				// Clean up the editor state before deleting the field
				const editor = this.fields[field.fieldId];
				if (editor) {
					editor.destroy();
					delete this.fields[field.fieldId];
				}
				field.Mutator.delete();
			}

			this.form.Mutator.clearType();
			this.form.Mutator.displayName('');
			this.form.Mutator.description('');

			// Reset selection to the form since all fields were deleted
			this.selectedFieldId = this.form.fieldId;
		}
	}

	private viewFields() {
		if (!this.form) {
			return null;
		}
		if (this.selectedFieldId == null) {
			this.selectedFieldId = this.form.fieldId;
		}
		return m('div', { className: CSS.field.container }, this.viewField(this.form));
	}

	private viewInfo() {
		if (this.selectedFieldId == null || this.fields[this.selectedFieldId] == null) {
			return m('div', { className: CSS.info.container });
		}

		return m('div', { className: CSS.info.container }, this.fields[this.selectedFieldId]?.viewInfo());
	}

	private createEditor(f: FormModelField) {
		if (f === this.form) {
			return new FormFieldEditorForm(this, f as any);
		}
		if (FormModelFieldSubForm.isSubForm(f)) {
			return new FormFieldEditorSubForm(this, f);
		}
		if (FormModelFieldNestedList.isNestedList(f)) {
			return new FormFieldEditorNestedList(this, f);
		}
		if (FormModelFieldSelectList.isSelectList(f)) {
			return new FormFieldEditorSelectList(this, f);
		}
		if (FormModelFieldLine.isLineField(f)) {
			return new FormFieldEditorLine(this, f);
		}
		if (f.type === IkeFieldType.HEIGHT) {
			return new FormFieldEditorHeight(this, f);
		}
		if (f.type === IkeFieldType.VERTICAL_SEPARATION) {
			return new FormFieldEditorVerticalSeparation(this, f);
		}
		return new FormFieldEditor(this, f);
	}

	@action createField(form: FormModelFieldSubForm, type: IkeFieldType) {
		const field = form.Mutator.create(type, IkeId.generate());
		// A new Line field is seeded with the fixed preset library and is required by default (UFO-582).
		if (FormModelFieldLine.isLineField(field)) {
			field.Mutator.options(FormModelFieldLine.defaultOptions());
		}
		this.setSelected(field);
		this.setScrollToField(field);
	}

	@action setScrollToField(field?: FormModelField) {
		if (field == null) {
			this.scrollToFieldId = null;
		} else {
			this.scrollToFieldId = field.fieldId;
		}
	}

	@action reset() {
		if (this.isEditing) {
			this.editState?.reset();
		}

		const editState = this.editState;
		for (const fieldId of Object.keys(this.fields)) {
			const fieldState = this.fields[fieldId];
			if (fieldState) {
				editState?.inputStates.remove(fieldState.state);
				fieldState.destroy();
			}
		}
		this.fields = {};
		if (this.form) {
			this.setSelected(this.form);
		}
	}

	get canSave(): boolean {
		if (!this.editState || !this.isEditing) {
			return false;
		}
		return (this.isNewForm || this.editState.isDirty) && !this.isSaving && this.editState.canSubmit;
	}

	@action.bound private checkExit(): boolean {
		return this.editState?.isDirty ?? false;
	}

	getSubFormEditors(): FormFieldEditorSubForm[] {
		return Object.values(this.fields).filter((e): e is FormFieldEditorSubForm => e instanceof FormFieldEditorSubForm);
	}

	clearCachedEditor(fieldId: string) {
		const editor = this.fields[fieldId];
		if (editor) {
			this.editState?.inputStates.remove(editor.state);
			editor.destroy();
			delete this.fields[fieldId];
		}
	}

	private getEditor(f: FormModelField) {
		let editor = this.fields[f.fieldId];
		if (editor == null) {
			editor = this.fields[f.fieldId] = this.createEditor(f);
			this.editState?.inputStates.add(editor.state);
		}
		return editor;
	}

	viewField(f: FormModelField) {
		const nodes = this.getEditor(f).viewTree();
		if (this.scrollToFieldId === f.fieldId && nodes) {
			nodes.attrs.oncreate = (ctx) => {
				ctx.dom.scrollIntoView();
				this.setScrollToField(undefined);
			};
		}
		return nodes;
	}

	@action setSelected(f: FormModelField) {
		if (f == null) {
			this.selectedFieldId = null;
		} else {
			this.selectedFieldId = f.fieldId;
		}
	}

	async save() {
		if (this.editState == null || !this.canSave) {
			return;
		}

		this.setSaving(true);

		let selectedFieldId: string | undefined;
		if (this.selectedFieldId != null) {
			selectedFieldId = this.selectedFieldId.split(':').pop();
		}

		try {
			const wasNewForm = this.isNewForm;
			const newForm = await Services.Form.save(this.editState.form);
			this.setForm(newForm);

			if (wasNewForm) {
				this.isNewForm = false;
				Services.Route.go(IkePageName.formEdit, { version: newForm.version });
			} else {
				Services.Route.setParam('version', newForm.version);

				if (selectedFieldId != null) {
					const selectedField = newForm.findField(selectedFieldId);
					if (selectedField) {
						this.setSelected(selectedField);
					}
				}
			}
		} catch (e: unknown) {
			NotificationService.error({ key: T.FORM_ERROR_SAVING });
			Services.Log.Logger.error(e as any);
		}

		this.setSaving(false);
	}

	@action private setSaving(isSaving: boolean) {
		this.isSaving = isSaving;
	}
}
